import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { STAKING_GUIDE } from '@/components/Airdrop/utils'
import RootButton from '@/components/Buttons/RootButton'
import Countdown from '@/components/Countdown'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import Collect from '@/components/Modals/Staking/ManageStakingModal/Collect'
import Stake from '@/components/Modals/Staking/ManageStakingModal/Stake'
import Withdraw from '@/components/Modals/Staking/ManageStakingModal/Withdraw'
import TooltipTitle from '@/components/TooltipTitle'
import { useBalancesContext } from '@/contexts/BalancesContext'
import { useEconomicsContext } from '@/contexts/EconomicsContext'
import { useStakingContext } from '@/contexts/StakingContext'
import useLoginOnAction from '@/hooks/useLoginOnAction'
import useStakingBot from '@/hooks/useStakingBot'
import { useAuth } from '@/plugins/auth'
import { convertTimestampDateAndTime, formatValueToSuffix } from '@/utils/utils'

interface StakeInfoRowProps {
  title: React.ReactNode
  value: string
  description?: string
  button: {
    text: string | React.ReactNode
    onClick: () => void
    disabled?: boolean
  }
}

const StakeInfoRow: React.FC<StakeInfoRowProps> = ({ title, value, description = '', button }) => {
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isSmallScreen = useMediaQuery('(max-width: 1000px)')

  return (
    <Stack>
      <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Stack style={{ flex: 1, gap: '5px' }}>
          {title}
          {description && (
            <Text
              style={{
                fontSize: '14px',
                maxWidth: isSmallScreen && !isMobile ? '' : '400px',
              }}
            >
              {description}
            </Text>
          )}
        </Stack>
        <Stack
          style={{
            flexDirection: 'column',
            backgroundColor: theme.colors.background2[0],
            borderRadius: '12px',
            padding: '8px',
            gap: '5px',
            alignItems: 'center',
          }}
        >
          <Title
            order={4}
            style={{
              color: theme.colors.paragraph[0],
              textAlign: 'right',
              alignContent: 'center',
              // height: '48px',
            }}
          >
            {value}
          </Title>
          <RootButton
            secondary
            style={{ width: '120px' }}
            style1={false}
            onClick={button.onClick}
            disabled={button.disabled}
          >
            {button.text}
          </RootButton>
        </Stack>
      </Stack>
    </Stack>
  )
}

const YourStaking: React.FC = () => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const { status } = useAuth()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isDownMd = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)

  const [opened, { open, close }] = useDisclosure(false)
  const [openedCollect, { open: openCollect, close: closeCollect }] = useDisclosure(false)
  const [openedManage, { open: openManage, close: closeManage }] = useDisclosure(false)

  // handle the navigation when user comes from sign out status
  const { handleNavigation: openManageStaking } = useLoginOnAction('staking', open)

  const {
    l1NetworkL1Balance,
    ll1: { withdrawable: ll1Withdrawable },
    rl1: { withdrawable: rl1Withdrawable },
  } = useBalancesContext()
  const {
    started,
    startDay,
    stakeMaxDays,
    stakeMultiplier,
    totalStakeSupply,
    rl1PerDay,
    rl1UnlockRate,
  } = useEconomicsContext()

  const { staked, stake, stakedList, claimableRewards } = useStakingContext()
  const { canCollect, nextCollectTimestamp } = useStakingBot()

  // Compute max reward per day per L1 staked (if staked for 10 years)
  const maxRewardPerDay = useMemo(() => {
    const stakeSupplyNumber = parseFloat(totalStakeSupply?.value.toString() || '1')
    // If total stake supply is 0, return rl1PerDay, since the first staker will get all rewards
    if (stakeSupplyNumber === 0) {
      return rl1PerDay / 1e18
    }
    // Otherwise, return the maximum reward per day per STAKE token assuming max staked days
    return (rl1PerDay * stakeMaxDays * stakeMultiplier) / stakeSupplyNumber
  }, [rl1PerDay, stakeMaxDays, stakeMultiplier, totalStakeSupply])

  // Compute current rewards
  const currentRewards = useMemo(() => {
    const stakeSupplyNumber = parseFloat(totalStakeSupply?.value.toString() || '1')
    const stakeNumber = parseFloat(stake?.value.toString() || '0')
    // If total stake supply is 0, return 0
    if (stakeSupplyNumber === 0) {
      return 0
    }
    return (stakeNumber * (rl1PerDay / 1e18)) / stakeSupplyNumber
  }, [stake, totalStakeSupply, rl1PerDay])

  // Components
  const IntroText = useMemo(
    () => (
      <Stack style={{ gap: '5px' }}>
        <Text>
          {`${started ? `Stake` : `Starting ${convertTimestampDateAndTime(startDay * 1000)} you will be able to stake`} your L1 to support the LAMINA1 network in exchange for staking power (STAKE) and Rewards L1 (RL1).`}
          &nbsp;&nbsp;&nbsp;
          <a
            style={{
              cursor: 'pointer',
              color: theme.colors.paragraph[0],
              textAlign: 'left',
              width: 'fit-content',
            }}
            href={STAKING_GUIDE}
            target="_blank"
            rel="noreferrer"
          >
            {t('components.staking.staking.stakingGuide', 'Staking Guide')}
          </a>
        </Text>
      </Stack>
    ),
    [started, startDay]
  )

  const CurrentStakingInfo = useMemo(
    () => (
      <Stack
        style={{
          backgroundColor: theme.colors.background2[0],
          borderRadius: 12,
          padding: '10px',
          margin: '5px 0px 0px -10px',
          gap: '20px',
        }}
      >
        <Stack
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <Stack style={{ gap: '15px' }}>
            <TooltipTitle
              order={4}
              title={t('components.staking.staking.earningRate', 'Daily Earning Rate')}
              tooltip={
                <Trans i18nKey="components.staking.staking.metric2Tooltip">
                  The amount of RL1 rewards tokens you are currently earning per day with your
                  staked L1.
                </Trans>
              }
            />
            <Stack
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '0px' : '10px',
                alignItems: 'baseline',
              }}
            >
              <Title order={4} style={{ color: theme.colors.paragraph[0] }}>
                {started ? formatValueToSuffix(Number(currentRewards), 4, 4) : '-'} RL1
              </Title>
              <Text style={{ fontSize: '14px' }}>
                {formatValueToSuffix(Number(stake.fullValue), 4, 4) || '-'} STAKE
              </Text>
            </Stack>
          </Stack>
          <RootButton
            secondary
            style={{ width: '120px' }}
            style1={false}
            onClick={openCollect}
            disabled={status !== 'connected' || !canCollect}
          >
            <Countdown
              futureDate={nextCollectTimestamp}
              countdownMessage={t('buttons.collect', 'Collect')}
            />
          </RootButton>
        </Stack>
      </Stack>
    ),
    [currentRewards, nextCollectTimestamp, canCollect, rl1UnlockRate, started, staked, t]
  )

  const ActionSet = useMemo(
    () => (
      <Stack style={{ flexDirection: 'column', marginTop: '15px' }}>
        <StakeInfoRow
          title={
            <TooltipTitle
              title={t('components.staking.staking.metric3', 'Active Stakes')}
              tooltip={t(
                'components.staking.staking.metric3Tooltip',
                'Manage your staked L1, batched by completion date. Completed stakes will continue to earn RL1 until withdrawn.'
              )}
              outside={!!isMobile}
            />
          }
          value={`${started ? staked.fullDisplay || '-' : '-'} L1`}
          description={`You have ${stakedList ? stakedList.length : '-'} active stake${stakedList && stakedList.length === 1 ? '' : 's'}`}
          button={{
            text: t('buttons.manage', 'Manage'),
            onClick: openManage,
            disabled: status !== 'connected' || !started || staked.value === BigInt(0),
          }}
        />
        <StakeInfoRow
          title={
            <TooltipTitle
              title={t('components.staking.staking.metric1', 'Available to Stake')}
              tooltip={t(
                'components.staking.staking.metric1Tooltip',
                'Idle/un-staked L1 you can put to use by staking, building, creating, or supporting others on LAMINA1. By staking L1, you can earn RL1 tokens that unwrap into useable L1 over time. You also earn STAKE tokens for every L1 you stake, which help multiply your rewards on the network.'
              )}
              outside={!!isMobile}
            />
          }
          value={`${started ? l1NetworkL1Balance.fullDisplay || '-' : '-'} L1`}
          button={{
            text: t('buttons.stake', 'Stake'),
            onClick: openManageStaking,
            disabled: !started,
          }}
          description={`Right now you could earn up to ${started ? (maxRewardPerDay.toFixed(2) === '0.00' ? `${(maxRewardPerDay * 30).toFixed(2)} RL1 per month per staked L1` : `${maxRewardPerDay.toFixed(2)} RL1 per day per staked L1`) : '- RL1 per day per staked L1'}`}
        />
      </Stack>
    ),
    [
      claimableRewards,
      stakedList,
      l1NetworkL1Balance,
      maxRewardPerDay,
      nextCollectTimestamp,
      openCollect,
      openManage,
      status,
      started,
      t,
    ]
  )

  // Modals
  const StakingModal = useMemo(
    () => (
      <ModalSkeleton
        opened={opened}
        onClose={close}
        size={isDownMd ? '' : '900px'}
        title={t('components.staking.staking.modal.stakeTitle', 'Stake')}
      >
        <Stake close={close} />
      </ModalSkeleton>
    ),
    [close, opened, t]
  )

  const WithdrawModal = useMemo(
    () => (
      <ModalSkeleton
        opened={openedManage}
        onClose={closeManage}
        size={isDownMd ? '' : '900px'}
        title={t('components.staking.staking.modal.manageTitle', 'Manage Staking')}
      >
        <Withdraw close={closeManage} />
      </ModalSkeleton>
    ),
    [closeManage, openedManage, t]
  )

  const CollectModal = useMemo(
    () => (
      <ModalSkeleton
        opened={openedCollect}
        onClose={closeCollect}
        size={isDownMd ? '' : '450px'}
        title={t('components.staking.staking.modal.collectTitle', 'Collect')}
      >
        <Collect
          close={closeCollect}
          claimableRewards={claimableRewards}
          ll1Withdrawable={ll1Withdrawable}
          rl1Withdrawable={rl1Withdrawable}
        />
      </ModalSkeleton>
    ),
    [claimableRewards, closeCollect, ll1Withdrawable, openedCollect, rl1Withdrawable]
  )

  return (
    <Stack style={{ gap: '15px', minWidth: '350px', marginBottom: '15px' }}>
      {/* Modals */}
      {StakingModal}
      {WithdrawModal}
      {CollectModal}
      {/* ------ */}
      <Title order={isMobile ? 3 : 2}>{t('components.staking.staking.title', 'Stake L1')}</Title>
      <Stack style={{ flexDirection: 'column' }}>
        {IntroText}
        {CurrentStakingInfo}
      </Stack>
      {ActionSet}
    </Stack>
  )
}

export default YourStaking
